import { platform, SECURE_KEY_REFRESH_TOKEN } from "@/platform";
import { getKnownStorefront } from "@/platform/storefront";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { usePushStore } from "@/stores/pushStore";
import { navigateTo } from "@/app/navigation";
import { t } from "@/i18n/ko";
import { analytics, commonProps } from "@/analytics";
import { onboardingStep } from "@/app/onboarding";
import { ep } from "./endpoints";
import type { ClientErrorCode, Me, RefreshResponse } from "./types";

export type AuthMode = "bearer" | "cookie";

export type ApiInit = Omit<RequestInit, "body" | "headers"> & {
  headers?: HeadersInit;
  /** JSON 직렬화할 본문. undefined면 본문 없음. */
  body?: unknown;
  /** Authorization 주입 안 함(auth/*). */
  noAuth?: boolean;
  /** 401이어도 refresh 하지 않음(refresh 자신·부팅 탐색). */
  noRefresh?: boolean;
  /** 0.5절 공통 분기(ACCOUNT_BLOCKED → /blocked 등)를 건너뛰고 호출부가 직접 처리한다(로그인 화면). */
  skipCommonErrors?: boolean;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly details: unknown;
  constructor(status: number, code: ClientErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

const API_BASE = import.meta.env.VITE_API_BASE ?? "";
const REFRESH_LOCK = "hamlak-refresh";

/** 모드는 호출 시점에 읽는다(테스트에서 clientKind를 바꾼다). */
export const authMode = (): AuthMode => (platform.clientKind === "web" ? "cookie" : "bearer");

/** 경로 → 절대 URL(동일 출처 상대 경로는 location.origin). openapi-fetch(spec.ts) baseUrl도 이 값을 쓴다. */
export function toUrl(path: string): string {
  if (API_BASE) return `${API_BASE}${path}`;
  // 상대 경로는 동일 출처. (jsdom·node fetch는 절대 URL을 요구하므로 location.origin을 붙인다)
  return typeof location !== "undefined" ? new URL(path, location.origin).toString() : path;
}

/**
 * 모든 요청의 식별 헤더(apiFetch·openapi-fetch 공용). X-Client 는 토큰 전달 방식, 나머지 셋은 서버 Amplitude 이벤트의
 * device_id(접두 없는 원래 값, 서버가 amplitudeDeviceId 적용)·app_version·bundle_version(AC0-182).
 */
export async function applyClientHeaders(headers: Headers): Promise<void> {
  headers.set("X-Client", platform.clientKind);
  // 마지막으로 읽은 기기 스토어 국가 — 서버가 가입 때 계정 국가 판정에 쓴다(상점·결제 설계 결정 16). 모르면 보내지 않는다
  const storefront = getKnownStorefront();
  if (storefront) headers.set("X-Storefront-Country", storefront);
  const deviceId = await platform.device.getDeviceId().catch(() => null);
  if (deviceId) headers.set("X-Device-Id", deviceId);
  const c = commonProps();
  headers.set("X-App-Version", c.app_version);
  headers.set("X-Bundle-Version", c.bundle_version);
}

/** 모든 요청의 공통 경로: 식별 헤더, Bearer 모드만 Authorization, JSON 본문. */
export async function rawFetch(path: string, init: ApiInit = {}): Promise<Response> {
  const { body, noAuth, noRefresh: _noRefresh, skipCommonErrors: _skip, headers: initHeaders, ...rest } = init;
  const headers = new Headers(initHeaders);
  await applyClientHeaders(headers);
  if (authMode() === "bearer" && !noAuth) {
    const at = useAuthStore.getState().accessToken;
    if (at) headers.set("Authorization", `Bearer ${at}`);
  }
  // cookie 모드는 Authorization 없음. 동일 출처라 credentials 기본값(same-origin)으로 충분하다.
  let serialized: string | undefined;
  if (body !== undefined) {
    headers.set("Content-Type", "application/json");
    serialized = JSON.stringify(body);
  }
  try {
    return await fetch(toUrl(path), serialized === undefined ? { ...rest, headers } : { ...rest, headers, body: serialized });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "NETWORK", t("error.network"), e);
  }
}

type ErrorEnvelope = { error?: { code?: unknown; message?: unknown; details?: unknown } };

export async function toApiError(res: Response): Promise<ApiError> {
  let code: ClientErrorCode = "UNKNOWN";
  let message = t("error.generic");
  let details: unknown;
  try {
    const body = (await res.json()) as ErrorEnvelope;
    if (typeof body?.error?.code === "string") code = body.error.code as ClientErrorCode;
    if (typeof body?.error?.message === "string" && body.error.message) message = body.error.message;
    details = body?.error?.details;
  } catch {
    // 본문이 JSON이 아니면 상태 코드만으로 간다.
  }
  return new ApiError(res.status, code, message, details);
}

/** 0.5절 공통 분기. 화면 공통 처리만 하고 던진다. */
export function handleCommonError(err: ApiError): void {
  if (err.status === 403 && err.code === "ACCOUNT_BLOCKED") {
    // 세션 폐기(앱은 토큰 삭제, 웹은 서버가 쿠키를 지워 내리므로 로컬 정리만). F06-15 다이얼로그는 /blocked에서.
    void clearLocalSession();
    navigateTo("/blocked?reason=blocked", { replace: true });
  } else if (err.status === 403 && err.code === "CSRF_REJECTED") {
    // 정상 빌드에서는 나오면 안 되는 클라 버그. Sentry 연동 전까지 콘솔 보고.
    console.error("[api] CSRF_REJECTED", err);
    useUiStore.getState().showSnackbar(t("error.generic"));
  } else if (err.status === 503 && err.code === "MAINTENANCE") {
    navigateTo("/blocked?reason=maintenance", { replace: true });
  }
}

async function parse<T>(res: Response, skipCommonErrors = false): Promise<T> {
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }
  const err = await toApiError(res);
  if (!skipCommonErrors) handleCommonError(err);
  throw err;
}

/** JSON API. 401이면 refresh 단일 비행 후 1회 재시도(Bearer·Cookie 공통). */
export async function apiFetch<T>(path: string, init: ApiInit = {}): Promise<T> {
  const res = await rawFetch(path, init);
  if (res.status !== 401 || init.noRefresh) return parse<T>(res, init.skipCommonErrors);
  await refreshOnce();
  return parse<T>(await rawFetch(path, init), init.skipCommonErrors);
}

// ---- refresh 단일 비행 ---------------------------------------------------------------

let inflight: Promise<void> | null = null;

/** 웹은 탭마다 JS 컨텍스트가 달라 inflight만으로는 탭 간 직렬화가 안 된다 → Web Locks. 미지원이면 그냥 실행. */
function withTabLock(fn: () => Promise<void>): Promise<void> {
  if (authMode() === "cookie" && typeof navigator !== "undefined" && "locks" in navigator && navigator.locks) {
    return navigator.locks.request(REFRESH_LOCK, fn).then(() => undefined);
  }
  return fn();
}

function nativeSecureStore() {
  const store = platform.device.secureStore;
  if (!store) throw new Error("secureStore is native-only");
  return store;
}

/** 로컬 세션 정리(앱: refresh 토큰 삭제, 웹: 서버가 쿠키를 지우므로 상태만). UNDER_AGE·ACCOUNT_BLOCKED·로그아웃 공용. */
export async function clearLocalSession(): Promise<void> {
  useAuthStore.getState().clear();
  analytics.setUserId(null); // device_id 는 유지(가입 전 이벤트와 이어지게)
  if (authMode() === "bearer") {
    try {
      await nativeSecureStore().remove(SECURE_KEY_REFRESH_TOKEN);
    } catch {
      // 저장소가 없어도 로컬 상태는 이미 비웠다.
    }
  }
}

async function logoutAndThrow(status = 401): Promise<never> {
  await clearLocalSession();
  throw new ApiError(status, "UNAUTHORIZED", t("error.generic"));
}

/**
 * refresh 회전. 동시 401 N개는 같은 비행을 기다린 뒤 각자 1회 재시도한다.
 * Bearer: 본문에 secureStore의 refresh 토큰. Cookie: 본문 없음(브라우저 쿠키).
 * 응답 tokens가 있으면 저장하고, 없으면(쿠키 회전) 아무것도 저장하지 않는다.
 */
export function refreshOnce(): Promise<void> {
  inflight ??= withTabLock(async () => {
    const mode = authMode();
    let body: { refreshToken: string } | undefined;
    if (mode === "bearer") {
      const rt = await nativeSecureStore().get(SECURE_KEY_REFRESH_TOKEN);
      if (!rt) await logoutAndThrow();
      body = { refreshToken: rt as string };
    }
    const r = await rawFetch(ep.authRefresh, { method: "POST", body, noAuth: true, noRefresh: true });
    if (!r.ok) await logoutAndThrow(r.status); // 회전 실패(유예창 밖 재사용 감지 포함) → 로그아웃
    const { tokens } = (await parse<RefreshResponse | undefined>(r)) ?? {};
    if (tokens) {
      useAuthStore.getState().setSession(tokens);
      await nativeSecureStore().set(SECURE_KEY_REFRESH_TOKEN, tokens.refreshToken);
    }
  }).finally(() => {
    inflight = null;
  });
  return inflight;
}

// ---- 세션 복원(bootstrap ③, auth-session 4.3) ------------------------------------------

/** 회원(온보딩 완료) 복원이면 user_id 를 잇는다. 온보딩 미완(pending)은 device_id 로만 */
const identify = (me: Me) => {
  if (me.user && onboardingStep(me.flags) === null) analytics.setUserId(me.user.id);
};

/** 세션이 있으면 /me를 받아 member로, 없거나 실패면 조용히 guest. */
export async function bootstrapSession(): Promise<Me | null> {
  const auth = useAuthStore.getState();
  try {
    if (authMode() === "bearer") {
      // 앱: refresh 토큰이 있으면 refresh 선행 → GET /me.
      const rt = await nativeSecureStore().get(SECURE_KEY_REFRESH_TOKEN);
      if (!rt) {
        auth.clear();
        return null;
      }
      await refreshOnce();
      const me = await apiFetch<Me>(ep.me, { noRefresh: true });
      useAuthStore.getState().setMember(me);
      identify(me);
      return me;
    }
    // 웹: GET /me 선행(access 쿠키가 살아 있으면 왕복 1회) → 401이면 refresh 1회 → GET /me.
    const me = await apiFetch<Me>(ep.me);
    useAuthStore.getState().setMember(me);
    identify(me);
    return me;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
}

/** 로그아웃: 서버 호출(실패 무시, 멱등) → 로컬 정리. 웹은 서버가 쿠키를 지운다. 앱은 이 기기의 푸시 토큰도 함께 보내 서버가 지운다(AC0-260). */
export async function logout(): Promise<void> {
  const pushToken = usePushStore.getState().token;
  try {
    const body = authMode() === "bearer" ? { refreshToken: (await nativeSecureStore().get(SECURE_KEY_REFRESH_TOKEN)) ?? "", ...(pushToken ? { pushToken } : {}) } : undefined;
    await rawFetch(ep.authLogout, { method: "POST", body, noRefresh: true });
  } catch {
    // 오프라인이면 재시도 큐 없이 로컬 정리만(auth-session 4.4).
  }
  usePushStore.getState().setToken(null);
  await clearLocalSession();
}
